import { EServerApplication } from '@/src/constants/constants'

type OrchestratorApplication = (typeof EServerApplication)[keyof typeof EServerApplication]

interface IStartServerRequest {
  roomName: string
  application?: OrchestratorApplication
  project?: string | null
  avatar?: string | null
}

/**
 * Резолвит значение `application` для запроса в оркестратор.
 *
 * Приоритет остаётся у `roomType` из `v/interview`.
 *
 * @param roomType тип комнаты из текущей frontend-логики
 */
const resolveOrchestratorApplication = (
  roomType: keyof typeof EServerApplication | null,
): OrchestratorApplication | null => {
  if (roomType) {
    return EServerApplication[roomType]
  }

  return null
}

const dedicatedAgentRoomTypes = new Set<string>([
  'voiceAssistant',
  'voiceAssistant2',
  'textAssistant',
  EServerApplication.voiceAssistant,
  EServerApplication.voiceAssistant2,
  EServerApplication.textAssistant,
])

/** Агент живёт на выделенной ноде; общий сервер не может заменить её при сбое. */
export const canUseSharedLivekitServer = (
  roomType: unknown,
  _project: string | null = null,
): boolean => !(typeof roomType === 'string' && dedicatedAgentRoomTypes.has(roomType))

const normalizeLivekitServerAddress = (livekitUrl: string): string => {
  const address = livekitUrl.trim().replace(/^(?:wss?|https?):\/\//i, '')

  try {
    // Явный порт :443 и завершающий слеш не делают общий LiveKit другой нодой.
    return new URL(`wss://${address}`).host.toLowerCase()
  } catch {
    return address.replace(/\/+$/, '').toLowerCase()
  }
}

/** Проверяем и успешный ответ оркестратора: он тоже не должен вести агента на общий сервер. */
export const canRouteRoomToLivekitUrl = (
  roomType: unknown,
  livekitUrl: string,
  sharedLivekitUrl: string,
  project: string | null = null,
): boolean =>
  canUseSharedLivekitServer(roomType, project) ||
  normalizeLivekitServerAddress(livekitUrl) !== normalizeLivekitServerAddress(sharedLivekitUrl)

export const checkIsCorrectLivekitUrl = (livekit_url: string) =>
  livekit_url?.split('.')?.length >= 2

export const startMeetServer = async (
  roomName: string,
  roomType: keyof typeof EServerApplication | null,
  project: string | null,
  avatar?: string | null,
) => {
  let livekit_url: string | null = null
  let status: number | null = null

  let body: IStartServerRequest = { roomName }
  const orchestratorApplication = resolveOrchestratorApplication(roomType)

  if (orchestratorApplication) {
    body = { ...body, application: orchestratorApplication }
  }

  if (project) {
    body = { ...body, project }
  }

  if (avatar) {
    body = { ...body, avatar }
  }

  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_MEET_API}/start_server`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: process.env.ORCHESTRATOR_TOKEN,
      },
      body: JSON.stringify(body),
    })

    status = response.status

    if (!response.ok) {
      throw new Error(`Error during request for starting meet server. Status: ${response.status}`)
    }

    let serverResponse
    try {
      serverResponse = await response.json()
      console.log(serverResponse)
    } catch (e) {
      throw new Error(
        `Error during parsing response to json after starting meet server request. Error: ${e.message}`,
      )
    }

    const isCorrectLivekitUrl =
      response.status === 200 && checkIsCorrectLivekitUrl(serverResponse?.ip_address)

    if (isCorrectLivekitUrl) {
      livekit_url = `wss://${serverResponse.ip_address}`
    } else if (canUseSharedLivekitServer(roomType, project)) {
      livekit_url = `wss://${process.env.NEXT_PUBLIC_LIVEKIT_URL}`
    }

    if (
      !canUseSharedLivekitServer(roomType, project) &&
      (!livekit_url || !canRouteRoomToLivekitUrl(
        roomType, livekit_url, `wss://${process.env.NEXT_PUBLIC_LIVEKIT_URL}`, project,
      ))
    ) {
      livekit_url = null
      status = 503
    }

    return { livekit_url, status }
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error with start meet server: \n${error}`)
    } else {
      console.error(`Unknown error with start meet server: \n${error}`)
    }

    if (!status) {
      status = 500
    }

    if (!livekit_url && canUseSharedLivekitServer(roomType, project)) {
      livekit_url = `wss://${process.env.NEXT_PUBLIC_LIVEKIT_URL}`
    }
  }

  return { livekit_url, status }
}

export const createMeetToken = async (roomName: string, origin: string) => {
  if (!roomName || !origin) {
    if (!roomName) {
      console.error(`Has not roomName into createMeetToken function`)
    }

    if (!origin) {
      console.error(`Has not origin into createMeetToken function`)
    }

    return
  }

  try {
    const customOrigin = origin

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000)

    const response = await fetch(`${customOrigin}/api/create_meet_token`, {
      method: 'POST',
      body: JSON.stringify({
        roomName,
        origin: customOrigin,
      }),
      signal: controller.signal,
    }).finally(() => clearTimeout(timeoutId))

    const result = await response?.json().catch((err) => {
      console.error(
        `Error with JSON response from create_meet_token: ${err?.message || 'Unknown error'}`,
      )
      throw new Error(
        `Error with JSON response from create_meet_token: ${err?.message || 'Unknown error'}`,
      )
    })

    return result as { meet_id: string; meet_token: string }
  } catch (e) {
    console.error(
      `Error with creating meet token. roomName is ${roomName}, origin is ${origin}. ${
        e?.message || "Couldn't find the error"
      }`,
    )
  }
}

export const getRoomType = (
  v: string | string[],
  interview?: boolean,
): keyof typeof EServerApplication | null => {
  const isSpecial = (query: string | string[] | boolean, value?: string) =>
    typeof query === 'string' && (value ? query === value : query === '' || query === 'true')

  if (interview) {
    return 'voiceAssistant2'
  }

  if (isSpecial(v, 'fa')) {
    return 'voiceAssistant'
  }

  if (isSpecial(v, 'ma')) {
    return 'voiceAssistant2'
  }

  if (isSpecial(v, 'hr')) {
    return 'textAssistant'
  }

  return null
}

export const getRoomName = (
  roomName: string,
  roomNameFromQuery?: string | string[],
  isIsolate?: boolean,
) => {
  if (roomNameFromQuery) {
    return `${roomName}-${roomNameFromQuery}`
  }

  if (isIsolate) {
    return `${roomName}-${Math.random()?.toString()?.split('.')?.at(1)}`
  }

  return roomName
}

export const convertLivekitUrlToWss = (livekit_url: string) => {
  let customLivekitUrl: string = ''

  if (livekit_url?.includes('https://')) {
    customLivekitUrl = livekit_url?.replace('https://', 'wss://')
  } else if (!livekit_url?.includes('wss://')) {
    customLivekitUrl = `'wss://'${livekit_url}`
  } else {
    customLivekitUrl = livekit_url
  }

  return customLivekitUrl
}
